import { getOrgSize, type Size } from "@/lib/getOrgSize";
import { SanityImageCrop, SanityImageHotspot } from "@/sanity/sanity.types";
import { ImageUrlBuilder } from "@sanity/image-url";
import type { ImageCrop, ImageHotspot } from "@sanity/types";
import { toNumber } from "lodash";

export const GLOBAL_POSTER_ASPECT = 271 / 384;

export type ImageProps = {
    priority?: boolean;
    altText?: string;
    className?: string;
    sizes?: string;
    width?: number | string;
    height?: number | string;
    crop?: boolean; // THIS IS CURRENTLY INTENDED FOR POSTERS
    isPosterAspect?: boolean;
    unoptimized?: boolean;

};

// The widths images are served in (srcset). Were next.config's images.deviceSizes when next/image built the srcset.
export const IMAGE_WIDTHS = [768, 1080, 1450];

type CMSImageAttributeOptions = {
    assetRef: string;
    imgUrlBuilder: ImageUrlBuilder;
    width?: number | string;
    height?: number | string;
    sizes?: string;
    unoptimized?: boolean;
    crop?: boolean;
    isPosterAspect?: boolean;
    imageHotspot?: SanityImageHotspot | ImageHotspot;
    imageCrop?: SanityImageCrop | ImageCrop;
    customCrop?: boolean;
};

export type CMSImageAttributes = {
    src: string;
    srcSet: string | undefined;
    sizes: string | undefined;
    width: number;
    height: number;
};

/**
 * The <img> attributes for a Sanity image, the way next/image built them with our loader:
 * with sizes, one srcset entry per IMAGE_WIDTHS; without, 1x and 2x rounded up to an image width.
 * Unoptimized gives a single URL (cropped to width x height when crop is set) and no srcset.
 */
export function getCMSImageAttributes({ assetRef, imgUrlBuilder, width, height, sizes, unoptimized = false, crop = false, isPosterAspect, imageHotspot, imageCrop, customCrop = false }: CMSImageAttributeOptions): CMSImageAttributes {
    const size = getOrgSize(assetRef);
    const w = toNumber(width ?? size.width);
    const h = Math.round(toNumber(height ?? (w / size.aspect)));
    const customCropAspect = w / h;

    if (unoptimized) {
        let imgBuilder = imgUrlBuilder.image(assetRef).auto("format");
        if (((height && width) || isPosterAspect) && crop) {
            if (width && height) {
                imgBuilder = imgBuilder.width(w).height(h);
            } else {
                imgBuilder = imgBuilder.width(w).height(Math.floor(w / GLOBAL_POSTER_ASPECT));
            }
            imgBuilder = doCrop(imgBuilder);
            imgBuilder = applyFocalPoint(imgBuilder, imageHotspot);
            imgBuilder = applyCroppedSize(imgBuilder, w, size, imageCrop, customCrop, customCropAspect);
        }
        return { src: imgBuilder.url(), srcSet: undefined, sizes, width: w, height: h };
    }

    const urlForWidth = (imgWidth: number) => {
        let builder = imgUrlBuilder.image(assetRef).width(imgWidth).auto("format");
        if (crop) {
            builder = doCrop(builder);
        }
        builder = applyFocalPoint(builder, imageHotspot);
        builder = applyCroppedSize(builder, imgWidth, size, imageCrop, customCrop, customCropAspect);
        return builder.url();
    };

    let candidates: { width: number; descriptor: string }[];
    if (sizes) {
        candidates = IMAGE_WIDTHS.map(imgWidth => ({ width: imgWidth, descriptor: `${imgWidth}w` }));
    } else {
        const roundUp = (target: number) => IMAGE_WIDTHS.find(imgWidth => imgWidth >= target) ?? IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];
        candidates = [...new Set([roundUp(w), roundUp(w * 2)])].map((imgWidth, i) => ({ width: imgWidth, descriptor: `${i + 1}x` }));
    }

    return {
        src: urlForWidth(candidates[candidates.length - 1].width),
        srcSet: candidates.map(c => `${urlForWidth(c.width)} ${c.descriptor}`).join(", "),
        sizes,
        width: w,
        height: h,
    };
}

export function applyFocalPoint(builder: ImageUrlBuilder, hotspot?: SanityImageHotspot | ImageHotspot) {
    let b = builder;
    if (hotspot?.x && hotspot?.y) {
        b = b.focalPoint(hotspot.x, hotspot.y)
    }
    return b;
}

export function applyCroppedSize(builder: ImageUrlBuilder, width: number, orgSize: Size, imageCrop?: SanityImageCrop | ImageCrop, customCrop?: boolean, customCropAspect?: number) {
    let b = builder;
    if (customCrop && customCropAspect) {
        const h = Math.trunc(width / customCropAspect);
        //console.debug("w, h", `${width}, ${h}`);
        b = b.width(width).height(h);
    } else if (imageCrop) {

        const h = width / orgSize.aspect;
        const cropH = (h - (h * imageCrop.bottom!)) - h * imageCrop.top!;

        const w = width;
        const cropW = (w - (w * imageCrop.right!)) - w * imageCrop.left!;
        b = b.height(Math.trunc(cropH)).width(Math.trunc(cropW));
    }
    return b;
}


export function doCrop(imageBuilder: ImageUrlBuilder): ImageUrlBuilder {
    return imageBuilder.fit("crop").crop("focalpoint");
}